<script lang="ts" setup>
const route = useRoute()
const router = useRouter()
let id = Number(route.params.id?.toString())

const screen = useScreen()
const { data: userInfo } = useAuth()

if (!id) {
  if (userInfo.value?.login) {
    id = userInfo.value.user.id
  } else {
    router.push('/')
  }
}

const isSelf = useState('is_account_self', () => userInfo.value?.user.id === id)

const { data: userDetail } = await useAsyncData(`userDetail:${id}`, () =>
  fetchUserDetailById(id)
)

if (!userDetail.value) {
  throw createError({ statusCode: 404, message: '找不到这名喵友' })
}

useHead({
  title: parseTitle(userDetail.value.name + '- 个人中心'),
  link: [
    {
      rel: 'canonical',
      href: `https://www.mfuns.net/member/${id}`
    },
    {
      rel: 'alternate',
      media: 'only screen and (max-width: 640px)',
      href: `https://m.mfuns.net/member/${id}`
    }
  ],
  meta: [
    {
      name: 'description',
      content: parseDescription(userDetail.value.bio)
    }
  ]
})

const { data: fansAndFollow } = useAsyncData(
  `userDetail:${id}:fansAndFollow`,
  () => fetchUserFollowAndFansById(id)
)

const menu: MenuItem[] = [
  {
    label: '首页',
    icon: 'lucide-newspaper',
    link: `/member/${id}`
  },
  {
    label: '视频',
    icon: 'lucide-film',
    link: `/member/${id}/videoList`,
    group: true
  },
  {
    label: '文章',
    icon: 'lucide-book-image',
    link: `/member/${id}/articleList`
  },
  {
    label: isSelf.value ? '我的关注' : 'TA的关注',
    icon: 'lucide-user-check',
    link: `/member/${id}/follow`,
    group: true
  },
  {
    label: isSelf.value ? '我的粉丝' : 'TA的粉丝',
    icon: 'lucide-users',
    link: `/member/${id}/fans`
  },
  {
    label: isSelf.value ? '我的收藏' : 'TA的收藏',
    icon: 'lucide-star',
    link: `/member/${id}/favorite`
  },
  {
    label: '历史记录',
    icon: 'lucide-history',
    link: '/member/history',
    hidden: !isSelf.value
  },
  {
    label: '我的道具',
    icon: 'lucide-shopping-bag',
    link: `/member/${id}/item`,
    hidden: !isSelf.value
  },
  {
    label: '我的徽章',
    icon: 'lucide-award',
    link: `/member/${id}/badge`,
    hidden: !isSelf.value
  },
  {
    label: '编辑资料',
    icon: 'lucide-user-cog',
    link: `/member/${id}/profile`,
    group: true,
    hidden: !isSelf.value
  },
  {
    label: '安全设置',
    icon: 'lucide-shield-check',
    link: `/member/${id}/safe`,
    hidden: !isSelf.value
  },
  {
    label: '会员中心',
    icon: 'lucide-crown',
    link: `/member/${id}/premium`,
    hidden: !isSelf.value
  },
  {
    label: '黑名单',
    icon: 'lucide-skull',
    link: `/member/${id}/blacklist`,
    hidden: !isSelf.value,
    disabled: true
  },
  {
    label: '注销账号',
    icon: 'lucide-trash-2',
    link: `/member/${id}/deleteAccount`,
    danger: true,
    group: true,
    hidden: !isSelf.value
  }
]

const sideMenu: MenuItem[] = [
  {
    label: '私信',
    icon: 'lucide-messages-square',
    hidden: isSelf.value,
    onClick: () => {
      open(`/message/whisper?uid=${id}`)
    }
  },
  {
    label: '举报用户',
    icon: 'lucide-flag',
    hidden: isSelf.value,
    onClick: () => {
      useEmitter().emit('openReportForm', {
        resourceId: id,
        resourceType: 5
      })
    }
  }
]

const banner = ref(userDetail.value?.banner_image)
const banReason = ref('')

onMounted(() => {
  getBanReason()
})

async function handleUpload(image?: { url?: string; file?: File }) {
  if (image?.url) {
    const res = await requestSetBanner(image.url)
    banner.value = image.url
  } else if (image?.file) {
    const res = await requestUploadImage(image.file)
    if (res.code === 1) {
      await requestSetBanner(res.data.file.file_path)
      banner.value = res.data.file.file_path
    }
  }
}

async function getBanReason() {
  if (userDetail.value?.ban_info) {
    const res = await fetchBanDetailById(userDetail.value.ban_info.id)
    banReason.value = res.data.info.info
  }
}

const maxExp = computed(
  () =>
    levelSection.find(
      (item) => item.level_id === (userInfo.value?.user.level_id || 0) + 1
    )?.experience || 1
)
const { data } = useAsyncData('userFollowAndFans', () =>
  fetchUserFollowAndFansById(userInfo.value?.user.id || 0)
)
const percentage = computed(() => {
  return Number(
    (((userInfo.value?.user.exp || 0) / maxExp.value) * 100).toFixed(2)
  )
})

const levelSection = [
  {
    id: 10,
    experience: 50000,
    level_id: 10,
    badges_id: 10
  },
  {
    id: 9,
    experience: 16000,
    level_id: 9,
    badges_id: 9
  },
  {
    id: 8,
    experience: 7500,
    level_id: 8,
    badges_id: 8
  },
  {
    id: 7,
    experience: 4000,
    level_id: 7,
    badges_id: 7
  },
  {
    id: 6,
    experience: 1800,
    level_id: 6,
    badges_id: 6
  },
  {
    id: 5,
    experience: 900,
    level_id: 5,
    badges_id: 5
  },
  {
    id: 4,
    experience: 500,
    level_id: 4,
    badges_id: 4
  },
  {
    id: 3,
    experience: 200,
    level_id: 3,
    badges_id: 3
  },
  {
    id: 2,
    experience: 50,
    level_id: 2,
    badges_id: 2
  },
  {
    id: 1,
    experience: 1,
    level_id: 1,
    badges_id: 1
  }
]
</script>

<template>
  <m-layout fullwidth nofooter class="m-account-center">
    <RContainer>
      <div class="m-account-center__container">
        <m-side-menu v-if="!screen.xs && !screen.sm" style="margin-right: 12px">
          <m-menu :menu="menu" center />
        </m-side-menu>
        <m-space vertical class="m-account-center__main">
          <div
            v-show="route.path == `/member/${id}`"
            class="m-account-center__header"
          >
            <div class="m-account-center__header-banner">
              <m-image-preview>
                <m-img
                  :key="banner"
                  class="m-account-center__header-banner_image preview"
                  :src="banner"
                />
              </m-image-preview>
              <div class="m-account-center__header-banner_mask"></div>
              <UploadImage
                v-if="userInfo?.user?.id === userDetail?.id"
                @update="handleUpload"
              >
                <div class="m-account-center__header-banner_upload">
                  <n-icon color="white" size="18" class="i-lucide:image-up" />
                </div>
              </UploadImage>
              <div class="m-account-center__header-banner_user">
                <RFlex :gap="4" direction="row">
                  <m-image-preview>
                    <UserAvatar
                      :id="userDetail!.id"
                      :key="userDetail!.avatar"
                      class="m-user__avatar preview"
                      :src="userDetail!.avatar"
                      :auth="userDetail!.user_auth"
                      :size="!screen.xs ? `xl3` : `xl`"
                    />
                  </m-image-preview>
                  <div style="width: 12px"></div>
                  <RFlex :gap="4" direction="column">
                    <div class="m-user__name">
                      <div
                        class="username"
                        :style="{
                          color: userDetail?.name_color
                            ? namecolorMap[userDetail.name_color] || '#fff'
                            : '#fff'
                        }"
                      >
                        {{ userDetail?.name }}
                      </div>
                      <m-badge
                        :size="!screen.xs ? `md` : `sm`"
                        :badges="userDetail?.badges || []"
                      />
                    </div>
                    <div>{{ pipe.date(userDetail?.created_at ?? 0) }} 注册</div>
                  </RFlex>
                  <div style="flex: 1"></div>
                </RFlex>
              </div>
              <m-follow-button
                :id="userDetail?.id || 0"
                class="m-account-center__header-banner_follow"
              />
            </div>
            <div class="m-account-center__header-user">
              <div class="m-account-center__header-user-bio">
                <div>{{ userDetail?.bio }}</div>
                <div
                  v-if="userDetail?.ban_info"
                  style="margin-top: 10px"
                  class="m-user__ban"
                >
                  <n-icon class="lucide-landmark" />
                  <div>账号封禁中</div>
                  <div>{{ banReason }}</div>
                </div>
              </div>
              <div class="hr"></div>
              <template v-if="userDetail?.user_auth">
                <RFlex
                  align="center"
                  justify="center"
                  class="m-account-center__header-user-auth"
                >
                  <RFlex style="flex: 1" class="text">
                    <div style="flex: 1; font-weight: bold; text-align: center">
                      {{ userDetail?.user_auth.name }}
                    </div>
                    <div style="flex: 2">{{ userDetail?.user_auth.desc }}</div>
                  </RFlex>
                </RFlex>
                <div class="hr"></div>
              </template>

              <RFlex
                justify="space-around"
                class="m-account-center__header-user-count"
              >
                <RFlex
                  direction="column"
                  :gap="0"
                  justify="center"
                  align="center"
                >
                  <div class="number">{{ pipe.num(fansAndFollow?.fans) }}</div>
                  <div class="label">粉丝数</div>
                </RFlex>
                <RFlex
                  direction="column"
                  :gap="0"
                  justify="center"
                  align="center"
                >
                  <div class="number">
                    {{ pipe.num(fansAndFollow?.follow) }}
                  </div>
                  <div class="label">关注数</div>
                </RFlex>
                <RFlex
                  v-if="isSelf"
                  direction="column"
                  :gap="0"
                  justify="center"
                  align="center"
                >
                  <div class="number">{{ userInfo.user.neko_coin }}</div>
                  <div class="label">喵币</div>
                </RFlex>
                <RFlex
                  v-if="isSelf"
                  direction="column"
                  :gap="0"
                  justify="center"
                  align="center"
                >
                  <div class="number">{{ userInfo.user.exp }}</div>
                  <div class="label">经验</div>
                </RFlex>
                <RFlex
                  direction="column"
                  :gap="0"
                  justify="center"
                  align="center"
                >
                  <div class="number">{{ userDetail?.id }}</div>
                  <div class="label">UID</div>
                </RFlex>
              </RFlex>
            </div>
          </div>
          <slot />
        </m-space>
        <m-space
          v-if="!screen.xs"
          style="margin-left: 12px"
          vertical
          class="m-account-center__side"
        >
          <m-menu v-if="!isSelf" style="width: 100%" :menu="sideMenu" center />
          <div class="m-account-center__side-section">
            <span>UID：{{ id }}</span>
          </div>
          <div class="m-account-center__side-section">
            <h4>
              <n-icon class="icon lucide-headphones" />
              客服中心
            </h4>
            <div class="content">
              <m-link size="12px" to="/customer/contact">联系客服</m-link>
            </div>
          </div>
          <div class="m-account-center__side-section">
            <h4>
              <n-icon class="icon lucide-pencil-ruler" />
              创作中心
            </h4>
            <div class="content">
              <m-link size="12px" to="/create/feed">发布动态</m-link>
              <m-link size="12px" to="/create/article">文章投稿</m-link>
              <m-link size="12px" to="/create/video">视频投稿</m-link>
              <m-link size="12px" to="/create">创作活动</m-link>
            </div>
          </div>
          <div class="m-account-center__side-section">
            <h4>
              <n-icon class="icon lucide-info" />
              关于我们
            </h4>
            <div class="content">
              <m-link
                size="12px"
                to="https://resource.mfuns.net/imgs/license/business-license.png"
              >
                营业执照
              </m-link>
              <m-link
                size="12px"
                to="https://www.mfuns.net/agree/userAgreement.html"
              >
                用户协议
              </m-link>
              <m-link size="12px" to="https://www.mfuns.net/agree/priv.html">
                隐私协议
              </m-link>
              <m-link size="12px" to="https://www.mfuns.net/doc/audit.html">
                审核规范
              </m-link>
            </div>
          </div>
          <div class="m-account-center__side-section">
            <h4>
              <n-icon class="icon lucide-flag" />
              举报中心
            </h4>
            <div class="content">
              <m-link size="12px" to="/report/bad">不良信息举报</m-link>
              <m-link size="12px" to="/report/plagiarism">侵权抄袭举报</m-link>
              <m-link size="12px" to="/report/teen">涉未成年举报</m-link>
              <m-link
                size="12px"
                to="http://www.shdf.gov.cn/shdf/channels/740.html"
              >
                扫黄打非举报
              </m-link>
            </div>
          </div>
          <div class="m-account-center__side-section">
            <MSideQualification />
          </div>
        </m-space>
      </div>
    </RContainer>
  </m-layout>
</template>

<style lang="scss" scoped>
.m-account-center {
  margin-bottom: 12px;

  &__container {
    display: flex;
    margin-top: 14px;
  }

  &__main {
    width: 0;
    max-width: 750px;
    flex: 1;
  }

  &__side {
    width: 240px;
    color: var(--color-gray-600);
    @media screen and (max-width: 1200px) {
      display: none;
    }
    &-section {
      background-color: #fff;
      border-radius: 4px;
      min-height: 20px;
      width: 100%;
      padding: 10px;

      h4 {
        width: 100%;
        color: var(--color-gray-600);
        margin-bottom: 12px;
        display: flex;
        font-size: 15px;
        font-weight: normal;
        align-items: center;
      }

      .icon {
        margin-right: 6px;
      }

      .content {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-gap: 8px;
        justify-items: center;
      }
    }
  }

  &__header {
    border-radius: 8px;
    overflow: hidden;

    &-banner {
      height: 250px;
      position: relative;
      @media screen and (max-width: 600px) {
        height: 180px;
      }

      &_image {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 0;
        height: 100%;
      }

      &_mask {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100px;
        background: linear-gradient(transparent, #000000b3);
      }

      &_upload {
        position: absolute;
        right: 16px;
        top: 16px;
        z-index: 2;
        border-radius: 50px;
        width: 45px;
        height: 45px;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: rgba(0, 0, 0, 0.6);
      }

      &_user {
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 3;
        right: 0;
        padding: 0 32px;
        color: #fff;
        transform: translateY(40%);
        @media screen and (max-width: 600px) {
          transform: translateY(30%);
        }
      }

      &_follow {
        position: absolute;
        right: 16px;
        bottom: 16px;
        z-index: 3;
      }
    }

    &-user {
      background-color: #fff;

      &-bio {
        padding: 25px 16px 25px 172px;
        color: var(--color-gray-600);
        @media screen and (max-width: 600px) {
          padding: 25px 16px 25px 132px;
        }
      }

      &-auth {
        padding: 20px 16px 20px 16px;
        color: var(--color-gray-600);
        .icon {
          width: 18px;
          height: 18px;
        }
      }

      &-count {
        padding: 20px 16px 20px 16px;

        .label {
          color: var(--color-gray-400);
          font-size: 12px;
        }

        .number {
          color: var(--color-gray-600);
          font-weight: bold;
          font-size: 15px;
        }
      }
    }
  }
}

.hr {
  height: 1px;
  width: 100%;
  background-color: var(--color-gray-200);
}

.m-user {
  display: flex;
  align-items: flex-start;
  margin: 20px;

  &__avatar {
    box-shadow: 1px 1px 3px #0003;
    border-radius: 150px;
  }

  .lucide-img {
    width: 20px;
    height: 20px;
    margin-right: 8px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-left: 12px;
  }

  &__exp {
    width: 100%;
    display: flex;
    align-items: center;

    > span {
      color: var(--color-gray-400);
      font-size: 12px;
      margin-left: 12px;
    }
  }

  &__auth,
  &__bio,
  &__ban {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    line-height: 20px;
    height: 20px;
  }

  &__ban {
    height: auto;
    flex-wrap: wrap;
  }

  &__name {
    height: 36px;
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    @media screen and (max-width: 600px) {
      height: 24px;
    }

    .username {
      margin-right: 8px;
      text-overflow: ellipsis;
      overflow: hidden;
      word-break: break-all;
      white-space: nowrap;
      font-size: 28px;
      font-weight: bold;
      line-height: 36px;
      text-shadow: 1px 1px 3px #0003;
      color: #fff;
      @media screen and (max-width: 600px) {
        font-size: 18px;
        line-height: 18px;
      }
    }
  }

  &__fans {
    margin-bottom: 8px;

    .item {
      margin-right: 12px;
      text-decoration: none;
      color: var(--color-gray-700);
      cursor: pointer;
    }

    .coin {
      margin-right: 12px;
      text-decoration: none;
      color: var(--color-gray-700);

      .number {
        color: var(--color-amber-500);
      }
    }

    .number {
      font-weight: bold;
      font-size: 16px;
    }
  }

  &__ban {
    padding: 4px 4px;
    color: var(--color-red-500);
    border: 1px solid var(--color-red-500);
    border-radius: 4px;
    font-size: 12px;
    display: flex;
    align-items: center;
    cursor: default;

    > div {
      margin: 0 4px;
    }
  }

  .text {
    color: var(--color-gray-500);
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }
}
</style>
